import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Wand2, Sparkles, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { key: "welcome", num: "01" },
  { key: "brand_voice", num: "02" },
  { key: "first_gen", num: "03" },
];

export default function Welcome() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const persisted = user?.onboarding_step;
  const initialStep = STEPS.some((s) => s.key === persisted) ? persisted : "welcome";
  const [step, setStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);

  const advance = async (nextStep, redirectTo, redirectState = null) => {
    setSaving(true);
    try {
      await api.patch("/onboarding", { step: nextStep });
      if (user) setUser({ ...user, onboarding_step: nextStep });
      if (redirectTo) {
        navigate(redirectTo, redirectState ? { state: redirectState } : undefined);
      } else {
        setStep(nextStep);
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* Left visual */}
      <div className="relative hidden overflow-hidden bg-neutral-100 lg:col-span-2 lg:block">
        <img
          src="https://images.unsplash.com/photo-1741806914412-340ca16e9175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHdoaXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTc2OTg1MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-white/10" />
        <div className="relative z-10 flex h-full flex-col justify-between p-10 text-black">
          <span className="font-serif text-2xl font-bold tracking-tight">
            PostShifted<span className="text-neutral-500">.</span>
          </span>
          <div>
            <p className="max-w-xs font-serif text-2xl leading-snug tracking-tight">
              &ldquo;Write once. Ship five channels. Never rewrite.&rdquo;
            </p>
            <p className="mt-4 text-xs uppercase tracking-widest text-neutral-600">— your new routine</p>
          </div>
          <div>
            <p className="mb-6 text-xs uppercase tracking-widest text-neutral-600">Setup progress</p>
            <div className="space-y-4">
              {STEPS.map((s, idx) => {
                const currentIdx = STEPS.findIndex((x) => x.key === step);
                const isDone = idx < currentIdx;
                const isActive = idx === currentIdx;
                return (
                  <div key={s.key} className="flex items-baseline gap-4">
                    <span className={`font-serif text-3xl ${isActive ? "text-black" : isDone ? "text-neutral-500" : "text-neutral-400"}`}>
                      {s.num}
                    </span>
                    <span className={`text-sm ${isActive ? "font-medium text-black" : "text-neutral-600"}`}>
                      {s.key === "welcome" && "Welcome"}
                      {s.key === "brand_voice" && "Brand Voice"}
                      {s.key === "first_gen" && "First remix"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Right content */}
      <div className="col-span-1 flex items-center justify-center bg-[#FAFAFA] px-6 py-16 lg:col-span-3">
        <div className="w-full max-w-lg">
          {step === "welcome" && (
            <div data-testid="onboarding-step-welcome">
              <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">— Welcome</p>
              <h1 className="font-serif text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                Turn one piece of content into posts for every platform.
              </h1>
              <p className="mt-6 text-base leading-relaxed text-neutral-600">
                Paste a blog post, transcript, or long-form note. In under a minute PostShifted turns it
                into a Twitter thread, LinkedIn post, carousel, Instagram caption, and email newsletter —
                each written to feel platform-native.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button
                  onClick={() => advance("brand_voice")}
                  disabled={saving}
                  data-testid="onboarding-get-started-btn"
                  className="h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  Get started
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
                <button
                  type="button"
                  onClick={() => advance("done", "/dashboard")}
                  data-testid="onboarding-skip-all"
                  className="text-sm text-neutral-500 underline underline-offset-4 hover:text-black"
                >
                  Skip the tour
                </button>
              </div>
            </div>
          )}

          {step === "brand_voice" && (
            <div data-testid="onboarding-step-brand-voice">
              <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">— Optional · 1 minute</p>
              <div className="mb-6 inline-flex items-center justify-center border border-neutral-200 bg-white p-4">
                <Wand2 className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h1 className="font-serif text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                Want better results?
              </h1>
              <p className="mt-6 text-base leading-relaxed text-neutral-600">
                Paste 2–4 pieces of writing you love and PostShifted will match your voice, cadence, and
                vocabulary on every remix. Takes about a minute.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button
                  onClick={() => advance("first_gen", "/brand-voice")}
                  disabled={saving}
                  data-testid="onboarding-setup-voice-btn"
                  className="h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  Set up my Brand Voice
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => advance("first_gen")}
                  disabled={saving}
                  data-testid="onboarding-skip-voice-btn"
                  className="h-12 rounded-none border-neutral-300 px-6"
                >
                  <SkipForward className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Skip for now
                </Button>
              </div>
            </div>
          )}

          {step === "first_gen" && (
            <div data-testid="onboarding-step-first-gen">
              <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">— Try it now</p>
              <div className="mb-6 inline-flex items-center justify-center border border-neutral-200 bg-white p-4">
                <Sparkles className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h1 className="font-serif text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                See it work in 20 seconds.
              </h1>
              <p className="mt-6 text-base leading-relaxed text-neutral-600">
                Tap the button below and we&apos;ll load a sample piece and generate your first
                remix automatically — no typing, no clicking. Watch it turn into a Twitter thread
                and a LinkedIn post live.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button
                  onClick={() => advance("done", "/dashboard", { onboarding_first_gen: true, auto_run: true })}
                  disabled={saving}
                  data-testid="onboarding-start-first-gen-btn"
                  className="h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Show me a live remix
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
                <button
                  type="button"
                  onClick={() => advance("done", "/dashboard")}
                  data-testid="onboarding-skip-first-gen"
                  className="text-sm text-neutral-500 underline underline-offset-4 hover:text-black"
                >
                  Skip and paste my own
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
